import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getStoredTheme,
  isTheme,
  resolveTheme,
  THEME_STORAGE_KEY,
  ThemeContext,
  type ResolvedTheme,
  type Theme,
} from './theme';

const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#0a0a0a',
};

const applyTheme = (effectiveTheme: ResolvedTheme) => {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(effectiveTheme);
  root.style.colorScheme = effectiveTheme;

  // index.html declara dos <meta theme-color>, una por preferencia del sistema.
  // El navegador aplica la PRIMERA cuyo media coincida, así que escribir solo
  // en la primera no servía de nada cuando el sistema iba en oscuro y la app en
  // claro (barra negra sobre app clara). Se escriben TODAS con el color del
  // tema efectivo: coincida la que coincida, el color ya es el correcto.
  window.document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => meta.setAttribute('content', THEME_COLORS[effectiveTheme]));
};

const persistTheme = (theme: Theme) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Persistence is optional: keep the selected theme active in this tab.
  }
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [effectiveTheme, setEffectiveTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredTheme()),
  );

  const setTheme = useCallback((nextTheme: Theme) => {
    setThemeState(nextTheme);
    persistTheme(nextTheme);
  }, []);

  useEffect(() => {
    const nextEffectiveTheme = resolveTheme(theme);
    applyTheme(nextEffectiveTheme);
    setEffectiveTheme(nextEffectiveTheme);
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const nextEffectiveTheme = resolveTheme('system');
      applyTheme(nextEffectiveTheme);
      setEffectiveTheme(nextEffectiveTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      setThemeState(isTheme(event.newValue) ? event.newValue : 'system');
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, effectiveTheme }),
    [theme, setTheme, effectiveTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
